/**
 * CPC Number Input Steppers
 * Wraps Custom Price Calculator number inputs in theme quantity-selector structure
 * so they inherit .quantity-selector styling (larger stepper buttons, inputmode for iPhone).
 */
(function () {
  "use strict";

  var ICON_MINUS =
    '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round"/></svg>';
  var ICON_PLUS =
    '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path class="vertical" d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round"/><path class="horizontal" d="M7 2.75L7 11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round"/></svg>';

  function getCalculator() {
    return document.getElementById("calculator");
  }

  /**
   * Parse link href into base URL and search params.
   */
  function parseBookNowLink(link) {
    var href = link.getAttribute("href") || "";
    var parts = href.split("?");
    return {
      base: parts[0],
      params: new URLSearchParams(parts[1] || ""),
    };
  }

  /**
   * Apply base URL and params to GHL booking / consult link hrefs.
   */
  function applyBookNowLinkHref(link, base, params) {
    var query = params.toString();
    link.href = base + (query ? "?" + query : "");
  }

  /**
   * Collect CPC form data, format as multiline notes, and append to booking / consult
   * link hrefs as ?notes=... for GHL form pre-fill.
   */
  function updateBookNowLinkWithFormData() {
    var calc = getCalculator();
    if (!calc) return;

    var lines = [];

    var priceEl =
      calc.querySelector(".price-marker") ||
      calc.querySelector("#calculator-final-value");
    if (priceEl) {
      var priceText = (priceEl.textContent || priceEl.innerText || "").trim();
      if (priceText) {
        lines.push("Quoted Price: " + priceText);
      }
    }

    var fields = calc.querySelectorAll(
      'input[name^="properties["], select[name^="properties["], textarea[name^="properties["]',
    );

    fields.forEach(function (field) {
      var name = field.getAttribute("name");
      if (!name || name.indexOf("properties[") !== 0) return;

      var key = name.slice(11, -1);
      var value;
      if (field.type === "checkbox" || field.type === "radio") {
        if (!field.checked) return;
        value = field.value || "Yes";
      } else {
        value = field.value;
      }
      if (key === "_cpc_custom_cart_item") return;
      if (value === "" || value == null || value == 0) return;
      lines.push(key + ": " + value);
    });

    var notes = lines.join("\n");
    var links = document.querySelectorAll(
      'a[href*="book-our-services"], a[href*="schedule-consultation"], a[href*="book-appointment"]',
    );

    links.forEach(function (link) {
      var parsed = parseBookNowLink(link);
      if (notes) parsed.params.set("notes", notes);
      parsed.params.set("services_interested_in", "Tint Services");
      applyBookNowLinkHref(link, parsed.base, parsed.params);
    });
  }

  function attachBookNowFormDataListeners() {
    var calc = getCalculator();
    if (!calc || calc.dataset.bookNowListenerAttached) return;

    function onFieldChange() {
      updateBookNowLinkWithFormData();
    }

    calc.addEventListener("change", onFieldChange);
    calc.addEventListener("input", onFieldChange);
    calc.dataset.bookNowListenerAttached = "true";
    updateBookNowLinkWithFormData();
  }

  function applyNumericKeypad() {
    var calc = getCalculator();
    if (!calc) return;
    var inputs = calc.querySelectorAll(
      'input.numInput-cpc, input.calc-prop.numInput-cpc, input[type="number"]',
    );
    inputs.forEach(function (input) {
      input.setAttribute("inputmode", "numeric");
      input.setAttribute("pattern", "[0-9]*");
    });
  }

  /**
   * Insert <br /> before each span.c-tooltip so label and tooltip sit on separate lines.
   */
  function insertBrBeforeTooltips() {
    var calc = getCalculator();
    if (!calc) return;
    var tooltips = calc.querySelectorAll("span.c-tooltip");
    tooltips.forEach(function (span) {
      var prev = span.previousSibling;
      if (prev && prev.nodeName === "BR") return;
      var br = document.createElement("br");
      span.parentNode.insertBefore(br, span);
    });
  }

  /**
   * Trigger change/input events on CPC elements that control conditional display.
   * Fixes: conditional fields not shown on initial load when default values are set.
   */
  function triggerConditionalDisplayRefresh() {
    var calc = getCalculator();
    if (!calc) return;

    var selects = calc.querySelectorAll("select.calc-prop");
    selects.forEach(function (sel) {
      sel.dispatchEvent(new Event("change", { bubbles: true }));
    });

    var checkboxes = calc.querySelectorAll('input[type="checkbox"].calc-prop');
    checkboxes.forEach(function (cb) {
      cb.dispatchEvent(new Event("change", { bubbles: true }));
    });

    if (typeof ChangeEvent === "function") {
      selects.forEach(function (sel) {
        try {
          ChangeEvent(null, sel);
        } catch (e) {}
      });
    }
  }

  /**
   * Run all CPC UI enhancements (steppers, keypad, tooltips, conditional refresh, book-now link).
   */
  function runCpcEnhancements() {
    wrapNumInputs();
    applyNumericKeypad();
    insertBrBeforeTooltips();
    triggerConditionalDisplayRefresh();
    attachBookNowFormDataListeners();
  }

  function wrapNumInputs() {
    var calc = getCalculator();
    if (!calc) return 0;

    var inputs = calc.querySelectorAll(
      "input.numInput-cpc:not(.cpc-stepper-wrapped)",
    );
    var wrapped = 0;

    inputs.forEach(function (input) {
      if (input.closest(".cpc-stepper-wrapper")) return;

      var wrapper = document.createElement("div");
      wrapper.className = "quantity quantity-selector cpc-stepper-wrapper";
      wrapper.style.position = "relative";

      var minusBtn = document.createElement("button");
      minusBtn.type = "button";
      minusBtn.name = "minus";
      minusBtn.className =
        "button quantity__button quantity-minus button-unstyled";
      minusBtn.setAttribute("aria-label", "Decrease");
      minusBtn.innerHTML =
        '<span class="svg-wrapper icon-minus">' + ICON_MINUS + "</span>";

      var plusBtn = document.createElement("button");
      plusBtn.type = "button";
      plusBtn.name = "plus";
      plusBtn.className =
        "button quantity__button quantity-plus button-unstyled";
      plusBtn.setAttribute("aria-label", "Increase");
      plusBtn.innerHTML =
        '<span class="svg-wrapper icon-plus">' + ICON_PLUS + "</span>";

      input.setAttribute("inputmode", "numeric");
      input.setAttribute("pattern", "[0-9]*");
      input.classList.add("cpc-stepper-wrapped");

      function stepDown() {
        var val = parseInt(input.value, 10) || 0;
        var min = parseInt(input.min, 10) || 0;
        input.value = Math.max(min, val - 1);
        input.dispatchEvent(new Event("change", { bubbles: true }));
        input.dispatchEvent(new Event("input", { bubbles: true }));
        if (typeof PriceUpdation === "function") PriceUpdation(input);
      }

      function stepUp() {
        var val = parseInt(input.value, 10) || 0;
        var max = parseInt(input.max, 10);
        var step = parseInt(input.step, 10) || 1;
        var newVal = val + step;
        if (!isNaN(max) && newVal > max) newVal = max;
        input.value = newVal;
        input.dispatchEvent(new Event("change", { bubbles: true }));
        input.dispatchEvent(new Event("input", { bubbles: true }));
        if (typeof PriceUpdation === "function") PriceUpdation(input);
      }

      minusBtn.addEventListener("click", stepDown);
      plusBtn.addEventListener("click", stepUp);

      var parent = input.parentNode;
      if (!parent) return;

      parent.insertBefore(wrapper, input);
      wrapper.appendChild(minusBtn);
      wrapper.appendChild(input);
      wrapper.appendChild(plusBtn);
      wrapped++;
    });

    applyNumericKeypad();
    return wrapped;
  }

  function run() {
    runCpcEnhancements();

    var attempts = 0;
    var iv = setInterval(function () {
      attempts++;
      runCpcEnhancements();
      if (attempts >= 25) clearInterval(iv);
    }, 400);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }

  var body = document.body;
  if (body) {
    var obs = new MutationObserver(function () {
      if (getCalculator()) runCpcEnhancements();
    });
    obs.observe(body, { childList: true, subtree: true });
  }

  setTimeout(runCpcEnhancements, 500);
  setTimeout(runCpcEnhancements, 1500);
  setTimeout(runCpcEnhancements, 3000);

  document.addEventListener("cpc-rendering-delay-configured", run);
  document.addEventListener("cpc-render-complete", run);
})();
